import '~/assets/styles/index.scss';

import React, { useEffect } from 'react';
import { GeoLocationProvider } from '~/app/context';
import { loadCtmScript, runCtmWhenReady } from '~/shared/lib/run-ctm';

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'cq_cmp',
  'cq_con',
  'cq_med',
  'cq_net',
  'cq_plt',
  'cq_src',
  'cq_term',
  'city',
];
const FIRST_TOUCH_KEY = 'analytics_first_touch_v1';
const LAST_TOUCH_KEY = 'analytics_last_touch_v1';
const ATTR_COOKIE_PREFIX = 'ctm_attr_';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

const readAttribution = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const url = new URL(window.location.href);
  const result = {};

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = url.searchParams.get(key);
    if (value) {
      result[key] = value;
    }
  });

  result.landing_url = url.href;
  result.landing_path = `${url.pathname}${url.search}`;
  result.referrer = document.referrer || '';

  return result;
};

const hasPaidParams = (attribution) => {
  return Boolean(
    attribution.gclid ||
      attribution.msclkid ||
      attribution.fbclid ||
      attribution.utm_source ||
      attribution.cq_src ||
      attribution.cq_cmp
  );
};

const setCookie = (name, value, maxAge = COOKIE_MAX_AGE) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
};

const setAttributionCookies = (firstTouch, lastTouch) => {
  const writeObject = (prefix, source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      setCookie(`${ATTR_COOKIE_PREFIX}${prefix}_${key}`, String(value));
    });
  };

  writeObject('first', firstTouch);
  writeObject('last', lastTouch);
};

const persistAttribution = (attribution) => {
  try {
    const firstTouchRaw = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (!firstTouchRaw) {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(attribution));
    }
    window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(attribution));
  } catch (error) {
    // No-op
  }
};

const readPersistedAttribution = () => {
  try {
    const firstTouch = JSON.parse(window.localStorage.getItem(FIRST_TOUCH_KEY) || '{}');
    const lastTouch = JSON.parse(window.localStorage.getItem(LAST_TOUCH_KEY) || '{}');
    return { firstTouch, lastTouch };
  } catch (error) {
    return { firstTouch: {}, lastTouch: {} };
  }
};

const primeDataLayer = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const currentAttribution = readAttribution();
  persistAttribution(currentAttribution);
  const { firstTouch, lastTouch } = readPersistedAttribution();
  setAttributionCookies(firstTouch, lastTouch);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'traffic_context_ready',
    page_url: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || '',
    source_data: {
      first_touch: firstTouch,
      last_touch: lastTouch,
    },
    first_touch: firstTouch,
    last_touch: lastTouch,
  });

  return currentAttribution;
};

const loadGtm = () => {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.__gtmLoaded) {
    return;
  }

  const gtmId = process.env.GTM_CODE;
  if (!gtmId) {
    return;
  }

  window.__gtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  script.onload = () => runCtmWhenReady();
  document.head.appendChild(script);
};

const ThirdPartyDeferredLoader = () => {
  useEffect(() => {
    const attribution = primeDataLayer();
    let timeoutId = null;

    const triggerLoad = () => {
      loadCtmScript();
      loadGtm();
      runCtmWhenReady();
      window.removeEventListener('scroll', triggerLoad);
      window.removeEventListener('pointerdown', triggerLoad);
      window.removeEventListener('touchstart', triggerLoad);
      window.removeEventListener('keydown', triggerLoad);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    loadCtmScript();
    loadGtm();
    runCtmWhenReady();

    window.addEventListener('scroll', triggerLoad, { once: true, passive: true });
    window.addEventListener('pointerdown', triggerLoad, { once: true, passive: true });
    window.addEventListener('touchstart', triggerLoad, { once: true, passive: true });
    window.addEventListener('keydown', triggerLoad, { once: true });

    if (hasPaidParams(attribution)) {
      timeoutId = setTimeout(triggerLoad, 50);
    } else {
      timeoutId = setTimeout(triggerLoad, 300);
    }

    const onPhoneClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest('a[href^="tel:"]');
      if (!anchor) {
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'phone_click',
        phone_href: anchor.getAttribute('href') || '',
      });
      triggerLoad();
    };

    document.addEventListener('click', onPhoneClick);

    return () => {
      window.removeEventListener('scroll', triggerLoad);
      window.removeEventListener('pointerdown', triggerLoad);
      window.removeEventListener('touchstart', triggerLoad);
      window.removeEventListener('keydown', triggerLoad);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('click', onPhoneClick);
    };
  }, []);

  return null;
};

export const wrapRootElement = ({ element }) => {
  return (
    <GeoLocationProvider>
      <ThirdPartyDeferredLoader />
      {element}
    </GeoLocationProvider>
  );
};
